/**
 * New Project Dialog Component
 * Modal for creating a new project with client selection/creation
 */

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { apiClient } from '../services/apiClient';

interface Client {
  id: string;
  name: string;
  industry: string | null;
}

interface NewProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectCreated: (projectId: string) => void;
}

export function NewProjectDialog({ isOpen, onClose, onProjectCreated }: NewProjectDialogProps) {
  const [step, setStep] = useState<'client' | 'project'>('client');
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Client form
  const [newClientName, setNewClientName] = useState('');
  const [newClientIndustry, setNewClientIndustry] = useState('');
  const [createNewClient, setCreateNewClient] = useState(false);

  // Project form
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectIndustry, setProjectIndustry] = useState('');
  const [proposalType, setProposalType] = useState<'sla' | 'milestones'>('milestones');
  const [llmModelId, setLlmModelId] = useState('us.amazon.nova-pro-v1:0');

  const proposalTypeOptions = [
    { value: 'milestones' as const, label: 'Milestones (Phased)' },
    { value: 'sla' as const, label: 'SLA (Service Levels)' },
  ];

  const modelOptions = [
    { value: 'us.amazon.nova-pro-v1:0', label: 'Amazon Nova Pro' },
    { value: 'arn:aws:bedrock:us-east-2:949701862374:inference-profile/global.anthropic.claude-sonnet-4-5-20250929-v1:0', label: 'Claude 4.5 Sonnet' },
    { value: 'arn:aws:bedrock:us-east-2:949701862374:inference-profile/global.anthropic.claude-opus-4-5-20251101-v1:0', label: 'Claude 4.5 Opus' },
  ];

  useEffect(() => {
    if (isOpen) {
      loadClients();
    }
  }, [isOpen]);

  const loadClients = async () => {
    try {
      const response = await apiClient.get<{ clients: Client[] }>('/api/clients/?page_size=100');
      setClients(response.clients || []);
      if (response.clients?.length === 0) {
        setCreateNewClient(true);
      }
    } catch (err: any) {
      console.error('Failed to load clients:', err);
      setCreateNewClient(true);
    }
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim()) {
      setError('Client name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post<Client>('/api/clients/', {
        name: newClientName.trim(),
        industry: newClientIndustry.trim() || null,
      });
      setSelectedClientId(response.id);
      setClients([...clients, response]);
      setCreateNewClient(false);
      setStep('project');
    } catch (err: any) {
      setError(err.message || 'Failed to create client');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectClient = () => {
    if (!selectedClientId) {
      setError('Please select a client');
      return;
    }
    setError(null);
    setStep('project');
  };

  const handleCreateProject = async () => {
    if (!projectName.trim()) {
      setError('Project name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post<{ id: string }>('/api/projects/', {
        name: projectName.trim(),
        client_id: selectedClientId,
        description: projectDescription.trim() || null,
        industry: projectIndustry.trim() || null,
        proposal_type: proposalType,
        llm_model_id: llmModelId,
      });
      onProjectCreated(response.id);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create project');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep('client');
    setSelectedClientId('');
    setNewClientName('');
    setNewClientIndustry('');
    setProjectName('');
    setProjectDescription('');
    setProjectIndustry('');
    setProposalType('milestones');
    setLlmModelId('us.amazon.nova-pro-v1:0');
    setError(null);
    setCreateNewClient(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <style>{`
        .dialog-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
        }

        .dialog-content {
          position: relative;
          background: var(--warm-white);
          border-radius: 12px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          max-width: 480px;
          width: 100%;
          margin: 1rem;
          max-height: 90vh;
          overflow-y: auto;
        }

        .dialog-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1.5px solid var(--sand);
          border-radius: 8px;
          background: var(--warm-white);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9375rem;
          color: var(--charcoal);
          transition: all 0.2s;
          outline: none;
        }

        .dialog-input:focus {
          border-color: var(--terracotta);
          box-shadow: 0 0 0 3px rgba(200, 88, 53, 0.1);
        }

        .dialog-button {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9375rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .dialog-button-primary {
          background: var(--terracotta);
          color: white;
          border: none;
        }

        .dialog-button-primary:hover:not(:disabled) {
          background: var(--terracotta-dark);
        }

        .dialog-button-secondary {
          background: transparent;
          color: var(--charcoal-light);
          border: 1.5px solid var(--sand);
        }

        .dialog-button-secondary:hover {
          border-color: var(--charcoal-light);
        }

        .dialog-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .client-option {
          padding: 0.75rem 1rem;
          border: 1.5px solid var(--sand);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .client-option:hover {
          border-color: var(--terracotta);
        }

        .client-option.selected {
          border-color: var(--terracotta);
          background: rgba(200, 88, 53, 0.05);
        }
      `}</style>

      <div className="dialog-overlay" onClick={handleClose} />

      <div className="dialog-content">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 style={{
              fontFamily: 'Crimson Text, Georgia, serif',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'var(--charcoal)',
            }}>
              {step === 'client' ? 'Select Client' : 'Create Project'}
            </h2>
            <button
              onClick={handleClose}
              className="p-1 rounded-md transition-colors"
              style={{ color: 'var(--charcoal-light)' }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-md" style={{
              background: '#FEF2F2',
              border: '1px solid #FCA5A5',
              color: '#991B1B',
              fontSize: '0.875rem',
            }}>
              {error}
            </div>
          )}

          {step === 'client' && (
            <div className="space-y-4">
              {!createNewClient && clients.length > 0 ? (
                <>
                  <p className="text-sm" style={{ color: 'var(--charcoal-light)' }}>
                    Select an existing client or create a new one
                  </p>

                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {clients.map((client) => (
                      <div
                        key={client.id}
                        className={`client-option ${selectedClientId === client.id ? 'selected' : ''}`}
                        onClick={() => setSelectedClientId(client.id)}
                      >
                        <p className="font-medium" style={{ color: 'var(--charcoal)' }}>
                          {client.name}
                        </p>
                        {client.industry && (
                          <p className="text-xs" style={{ color: 'var(--charcoal-light)' }}>
                            {client.industry}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => setCreateNewClient(true)}
                    className="text-sm"
                    style={{ color: 'var(--terracotta)', textDecoration: 'underline' }}
                  >
                    + Create new client
                  </button>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={handleClose}
                      className="dialog-button dialog-button-secondary flex-1"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSelectClient}
                      className="dialog-button dialog-button-primary flex-1"
                      disabled={!selectedClientId}
                    >
                      Continue
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm" style={{ color: 'var(--charcoal-light)' }}>
                    Create a new client to get started
                  </p>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--charcoal)' }}>
                        Client Name *
                      </label>
                      <input
                        type="text"
                        className="dialog-input"
                        placeholder="Acme Corporation"
                        value={newClientName}
                        onChange={(e) => setNewClientName(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--charcoal)' }}>
                        Industry
                      </label>
                      <input
                        type="text"
                        className="dialog-input"
                        placeholder="Technology, Retail, Finance..."
                        value={newClientIndustry}
                        onChange={(e) => setNewClientIndustry(e.target.value)}
                      />
                    </div>
                  </div>

                  {clients.length > 0 && (
                    <button
                      onClick={() => setCreateNewClient(false)}
                      className="text-sm"
                      style={{ color: 'var(--terracotta)', textDecoration: 'underline' }}
                    >
                      ← Back to client list
                    </button>
                  )}

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={handleClose}
                      className="dialog-button dialog-button-secondary flex-1"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateClient}
                      className="dialog-button dialog-button-primary flex-1"
                      disabled={isLoading || !newClientName.trim()}
                    >
                      {isLoading ? 'Creating...' : 'Create Client'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {step === 'project' && (
            <div className="space-y-4">
              <p className="text-sm" style={{ color: 'var(--charcoal-light)' }}>
                Enter project details
              </p>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--charcoal)' }}>
                    Project Name *
                  </label>
                  <input
                    type="text"
                    className="dialog-input"
                    placeholder="E-commerce Platform Redesign"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--charcoal)' }}>
                    Description
                  </label>
                  <textarea
                    className="dialog-input"
                    rows={3}
                    placeholder="Brief description of the project..."
                    value={projectDescription}
                    onChange={(e) => setProjectDescription(e.target.value)}
                    style={{ resize: 'none' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--charcoal)' }}>
                    Industry
                  </label>
                  <input
                    type="text"
                    className="dialog-input"
                    placeholder="Technology, Retail, Finance..."
                    value={projectIndustry}
                    onChange={(e) => setProjectIndustry(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--charcoal)' }}>
                    Proposal Type
                  </label>
                  <select
                    className="dialog-input"
                    value={proposalType}
                    onChange={(e) => setProposalType(e.target.value as any)}
                  >
                    {proposalTypeOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--charcoal)' }}>
                    Model
                  </label>
                  <select
                    className="dialog-input"
                    value={llmModelId}
                    onChange={(e) => setLlmModelId(e.target.value)}
                  >
                    {modelOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setStep('client')}
                  className="dialog-button dialog-button-secondary flex-1"
                >
                  Back
                </button>
                <button
                  onClick={handleCreateProject}
                  className="dialog-button dialog-button-primary flex-1"
                  disabled={isLoading || !projectName.trim()}
                >
                  {isLoading ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
